import React, { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";

import Modal from "../../../components/modal/Modal";
import {
  createSupplier,
  getAllSuppliers,
  updateSupplier,
  deleteSupplier,
} from "../../../service/api";
import { Trash2 } from "lucide-react";
import { emptyForm, supplierFormData } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const SupplierData: React.FC = () => {
  const [rowData, setRowData] = useState<supplierFormData[]>([]);
  const [originalData, setOriginalData] = useState<supplierFormData[]>([]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [form, setForm] = React.useState<supplierFormData>(emptyForm);
  const [errors, setErrors] = useState<
    Partial<Record<keyof supplierFormData, string>>
  >({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const isView = modalMode === "view";
  const isEdit = modalMode === "edit";
  const title = {
    create: "Create Supplier",
    edit: "Edit Supplier",
    view: "View Supplier",
  }[modalMode];

  const handleCreate = () => {
    setModalMode("create");
    setForm(emptyForm);
    setSelectedSupplier(null);
    setModalOpen(true);
    setErrors({});
  };

  const handleView = (supplier: any) => {
    setModalMode("view");
    setSelectedSupplier(supplier);
    setForm({ ...supplier });
    setModalOpen(true);
    setErrors({});
  };

  const handleEdit = (supplier: any) => {
    const editableSupplier: supplierFormData = {
      name: supplier.name || "",
      phoneNumber: supplier.phoneNumber || "",
      email: supplier.email || "",
      address: supplier.address || "",
    };

    setForm(editableSupplier);
    setModalMode("edit");
    setSelectedSupplier(supplier);
    setModalOpen(true);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    Object.entries(form).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        newErrors[key] = `${key} is required`;
      }
    });

    if (form.phoneNumber && !/^\d{10}$/.test(form.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be exactly 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    // Fetch suppliers from API
    const fetchSuppliers = async () => {
      setIsLoading(true);
      try {
        const res = await getAllSuppliers();
        setRowData(res.data.data || []);
        setOriginalData(res.data.data || []);
      } catch (error) {
        // handle error (show toast, etc.)
      } finally {
        setIsLoading(false);
      }
    };
    fetchSuppliers();
  }, []);

  const handleModalSubmit = async (data: supplierFormData) => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      if (modalMode === "create") {
        await createSupplier(data);
      } else if (modalMode === "edit" && selectedSupplier) {
        // @ts-ignore
        await updateSupplier(selectedSupplier.id, data);
      }
      // Refresh supplier list
      const res = await getAllSuppliers();
      setRowData(res.data.data || []);
      setOriginalData(res.data.data || []);
      setModalOpen(false);
      setForm(emptyForm);
      setSelectedSupplier(null);
    } catch (error) {
      // handle error (show toast, etc.)
    } finally {
      setIsLoading(false);
    }
  };

  const searchData = async (keyword: any) => {
    if (!keyword.trim()) {
      setRowData(originalData);
      return;
    }

    const filteredData = originalData.filter((row) =>
      Object.values(row).some((value) =>
        String(value)?.toLowerCase().includes(keyword.toLowerCase())
      )
    );
    setRowData(filteredData);
  };

  const handleDelete = async (supplier: any) => {
    if (!window.confirm("Are you sure you want to delete this supplier?"))
      return;
    setIsLoading(true);
    try {
      // @ts-ignore
      await deleteSupplier(supplier.id);
      const res = await getAllSuppliers();
      setRowData(res.data.data || []);
      setOriginalData(res.data.data || []);
    } catch (error) {
      // handle error
    } finally {
      setIsLoading(false);
    }
  };

  const [columnDefs, setColDefs] = useState<any>([
    {
      field: "name",
      headerName: "Supplier Name",
      sortable: false,
      minWidth: 150,
      maxWidth: 200,
      flex: 1,
      headerStyle: {
        backgroundColor: "#e9f5ff",
      },
      filter: false,
      cellStyle: {
        textAlign: "left",
        padding: "10px 0 0 15px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "name",
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      sortable: false,
      minWidth: 120,
      maxWidth: 150,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 0 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "phoneNumber",
    },
    {
      field: "email",
      headerName: "Email",
      sortable: false,
      minWidth: 150,
      maxWidth: 200,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 0 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "email",
    },
    {
      field: "address",
      headerName: "Address",
      sortable: false,
      minWidth: 200,
      flex: 2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 0 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "address",
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 10px 0 5px ", textAlign: "right" },
      minWidth: 100,
      maxWidth: 120,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellRenderer: (params: any) => (
        <div className="flex gap-x-1 pt-1 pb-1">
          <button
            onClick={() => handleView(params.data)}
            className="p-1 hover:bg-red-100 rounded"
            title="View Supplier"
          >
            <img className="iconTd w-5 h-5" src={images.viewIcon} alt="view" />
          </button>
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit Supplier"
          >
            <img className="iconTd w-5 h-5" src={images.editIcon} alt="edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Delete Supplier"
          >
            <Trash2 className="w-5 h-5 text-red-500" />
          </button>
        </div>
      ),
      filter: false,
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
        bgColor: "#1f2937",
        textColor: "#fff",
      },
    };
  }, []);
  const theme = useMemo(() => {
    return gridTheme;
  }, []);
  return (
    <>
      <div className="px-2 py-4 mt-14 ml-2 min-h-100vh">
        <div className="flex justify-between items-center">
          <h2 className="mb-3">Supplier Management</h2>

          <button
            onClick={handleCreate}
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-3 py-2 rounded-full"
          >
            <img src={images.createUserImg} width={20} alt="create-supplier" />
            CREATE SUPPLIER
          </button>
        </div>
        <div className="input-affix w-25 mb-3">
          <i className="prefix-icon anticon anticon-search"></i>
          <input
            type="text"
            className="form-control"
            onChange={(e) => searchData(e.target.value)}
            placeholder="Search Here..."
          />
        </div>

        <div className="tableBlock w-full ">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={isLoading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No Suppliers added yet
                </p>
                <button
                  onClick={handleCreate}
                  className="font-semibold bg-[#0fa9a6] text-white px-4 py-2 rounded-full transition-colors"
                >
                  Add Supplier
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-2xl rounded-xl shadow-lg relative"}
      >
        {
          <>
            <div className="pt-6">
              <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
                {title}
              </h2>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleModalSubmit(form);
                }}
              >
                <div className="grid grid-cols-1 gap-4 px-6 pb-6">
                  {(
                    [
                      ["Supplier Name", "name"],
                      ["Phone Number", "phoneNumber"],
                      ["Supplier Email", "email"],
                      ["Address", "address"],
                    ] as [string, keyof supplierFormData][]
                  ).map(([label, name]) => {
                    return (
                      <div key={name}>
                        <label className="text-sm block mb-1">{label}</label>
                        {name === "address" ? (
                          <textarea
                            name={name}
                            value={form[name]}
                            onChange={handleChange}
                            disabled={isView}
                            placeholder={`Enter ${label}`}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#375998] h-24"
                          />
                        ) : (
                          <input
                            type="text"
                            name={name}
                            value={form[name]}
                            onChange={handleChange}
                            disabled={isView}
                            placeholder={`Enter ${label}`}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                          />
                        )}
                        {errors[name] && (
                          <p className="text-sm text-red-500 mt-1 capitalize">
                            {errors[name]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
                  <button
                    onClick={() => {
                      setModalOpen(false);
                      setForm(emptyForm);
                    }}
                    type="button"
                    className="bg-[#8497B0]  text-white px-4 py-2 rounded-full hover:opacity-90"
                  >
                    CANCEL
                  </button>
                  {!isView && (
                    <button
                      type="submit"
                      className="bg-[#00A8A8]  text-white px-4 py-2 rounded-full hover:opacity-90"
                    >
                      SUBMIT
                    </button>
                  )}
                </div>
              </form>
            </div>
          </>
        }
      </Modal>
    </>
  );
};

export default SupplierData;
